import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { useBasePath } from "@/hooks/useBasePath";
import API from "@/lib/api";
import { toast } from "@/lib/notify";
import { Switch } from "@/components/ui/switch";
import {
  ArrowLeft, Save, Upload, CheckCircle2, Clock, AlertCircle,
  Mail, Plus, Trash2, FileText, Download, Tag, BarChart3, Info,
  UserCheck, UserX, Shield, Eye,
} from "lucide-react";
import { EmailConfirmDialog } from "@/components/EmailConfirmDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { filterByInvestorType } from "@/lib/investorTypes";
import { TaxFormTab } from "@/components/TaxFormTab";
import { SectionPdfButton } from "@/pages/ComplianceListPage";
import { SubAgreementExtractCard } from "@/components/SubAgreementExtractCard";

const ONBOARDING_STYLES = {
  pending: { icon: Clock, color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" },
  in_progress: { icon: AlertCircle, color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/20" },
  submitted: { icon: Upload, color: "text-violet-400", bg: "bg-violet-500/10 border-violet-500/20" },
  complete: { icon: CheckCircle2, color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" },
};

const LABEL_OPTIONS = ["Primary", "Legal", "Tax Advisor", "Personal", "Compliance", "Assistant", "Other"];

export default function InvestorDetailPage() {
  const { investorId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [investor, setInvestor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [personalInfo, setPersonalInfo] = useState({});
  const [distEmails, setDistEmails] = useState([]);
  const [uploading, setUploading] = useState({});
  const fileRefs = useRef({});
  const [statementPeriods, setStatementPeriods] = useState([]);
  const [downloadingStmt, setDownloadingStmt] = useState(null);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [outgoingEmailEnabled, setOutgoingEmailEnabled] = useState(false);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [investorDisplayId, setInvestorDisplayId] = useState("");

  // Email confirm state
  const [emailConfirm, setEmailConfirm] = useState({ open: false, action: null, description: "" });

  // Investor login account state (MP-1)
  const [userAccount, setUserAccount] = useState(null);
  const [userAccountLoading, setUserAccountLoading] = useState(false);
  const [loginConfirm, setLoginConfirm] = useState({ open: false, action: null, title: "", description: "" });

  // Owner reassignment state
  const [superAdmins, setSuperAdmins] = useState([]);
  const [ownerChanging, setOwnerChanging] = useState(false);
  const [pendingOwnerId, setPendingOwnerId] = useState(null);

  const basePath = useBasePath();
  const isSA = user?.role === "super_admin";
  const isFM = user?.role === "admin";
  // FM edit access is gated by fund_config.fm_can_edit_investor (default true to preserve old behaviour
  // on funds that haven't configured the toggle). Super Admin always has edit access.
  const fundConfig = investor?.fund?.fund_config || {};
  const fmEditAllowed = fundConfig.fm_can_edit_investor !== false; // undefined/missing => allowed
  const canEdit = isSA || (isFM && fmEditAllowed);

  const loadInvestor = useCallback(async () => {
    try {
      const { data } = await API.get(`/investors/${investorId}`);
      setInvestor(data);
      setPersonalInfo(data.personal_info || {});
      setDistEmails(data.distribution_emails || []);
      setOutgoingEmailEnabled(data.outgoing_email_enabled ?? false);
      setSelectedClasses(data.classes || []);
      setInvestorDisplayId(data.investor_display_id || data.id?.slice(0, 8).toUpperCase() || "");
      // Load statement periods
      try {
        const periodsRes = await API.get(`/investors/${investorId}/statement-periods`);
        setStatementPeriods(periodsRes.data.periods || []);
      } catch { /* no statements available */ }
    } catch {
      toast.error("Failed to load investor");
      navigate(`${basePath}/investors`);
    } finally {
      setLoading(false);
    }
  }, [investorId, basePath, navigate]);

  useEffect(() => { loadInvestor(); }, [loadInvestor]);

  // Load list of SAs for owner reassignment (SA only)
  useEffect(() => {
    if (user?.role !== "super_admin") return;
    API.get("/users?role=super_admin&status=active").then(res => setSuperAdmins(res.data)).catch(() => {});
  }, [user?.role]);

  const changeOwner = async (newOwnerId) => {
    setOwnerChanging(true);
    try {
      await API.patch(`/investors/${investorId}/owner?new_owner_id=${newOwnerId}`);
      toast.success("Owner updated");
      loadInvestor();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change owner");
    } finally {
      setOwnerChanging(false);
      setPendingOwnerId(null);
    }
  };

  const confirmOwnerChange = (newOwnerId) => {
    if (newOwnerId === investor?.owner_id) return;
    setPendingOwnerId(newOwnerId);
  };

  // Load investor login account info (SA only)
  const loadUserAccount = useCallback(async () => {
    if (user?.role !== "super_admin") return;
    try {
      const { data } = await API.get(`/investors/${investorId}/user-account`);
      setUserAccount(data);
    } catch { /* no account */ }
  }, [investorId, user?.role]);

  useEffect(() => { loadUserAccount(); }, [loadUserAccount]);

  const toggleLoginStatus = async () => {
    setUserAccountLoading(true);
    try {
      const { data } = await API.patch(`/investors/${investorId}/user-account/toggle-status`);
      toast.success(data.message);
      loadUserAccount();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle login status");
    } finally {
      setUserAccountLoading(false);
    }
  };

  const saveDetails = async () => {
    setSaving(true);
    try {
      const validEmails = distEmails.filter(e => e.email.trim());
      await API.put(`/investors/${investorId}`, {
        personal_info: personalInfo,
        distribution_emails: validEmails,
        outgoing_email_enabled: outgoingEmailEnabled,
      });
      toast.success("Investor updated");
      loadInvestor();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const saveTaxForm = async (section, values, newName) => {
    try {
      await API.put(`/investors/${investorId}`, { tax_forms: { [section]: values }, ...(newName ? { name: newName } : {}) });
      toast.success(`${section.toUpperCase()} details updated`);
      loadInvestor();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const saveOfferings = async () => {
    setSaving(true);
    try {
      await API.put(`/investors/${investorId}`, { classes: selectedClasses });
      toast.success("Offerings updated");
      loadInvestor();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const uploadDoc = async (docId, fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0) return;
    setUploading(prev => ({ ...prev, [docId]: true }));
    let okCount = 0;
    const failures = [];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        await API.post(`/investors/${investorId}/documents/${docId}`, formData);
        okCount += 1;
      } catch (err) {
        failures.push({ name: file.name, reason: err.response?.data?.detail || "upload failed" });
      }
    }
    if (okCount > 0 && failures.length === 0) {
      toast.success(okCount === 1 ? "Document uploaded" : `${okCount} files uploaded`);
    } else if (okCount > 0) {
      toast.warning(`${okCount} uploaded, ${failures.length} failed`);
    } else {
      toast.error(`Upload failed: ${failures[0]?.reason || "unknown error"}`);
    }
    loadInvestor();
    setUploading(prev => ({ ...prev, [docId]: false }));
  };

  const deleteDocFile = async (docId, fileId, fileName) => {
    if (!window.confirm(`Remove ${fileName || "this file"} from the slot?`)) return;
    try {
      await API.delete(`/investors/${investorId}/documents/${docId}/files/${fileId}`);
      toast.success("File removed");
      loadInvestor();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove file");
    }
  };

  const downloadDoc = async (docId, fileName, fileId) => {
    try {
      const params = fileId ? `?file_id=${fileId}` : "";
      const response = await API.get(`/investors/${investorId}/documents/${docId}/download${params}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error("Download failed");
    }
  };

  const previewDoc = async (docId, fileId) => {
    try {
      const fileParam = fileId ? `&file_id=${fileId}` : "";
      const response = await API.get(`/investors/${investorId}/documents/${docId}/download?preview=1${fileParam}`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: response.headers["content-type"] || "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch {
      toast.error("Preview not available");
    }
  };

  const addDistEmail = () => setDistEmails(prev => [...prev, { email: "", label: "Primary" }]);
  const updateDistEmail = (i, key, val) => setDistEmails(prev => prev.map((e, idx) => idx === i ? { ...e, [key]: val } : e));
  const removeDistEmail = (i) => setDistEmails(prev => prev.filter((_, idx) => idx !== i));

  const downloadStatement = async (periodId, periodLabel) => {
    setDownloadingStmt(periodId);
    try {
      const response = await API.get(`/investors/${investorId}/statement/${periodId}`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${investor.name?.replace(/ /g, "_")}_-_${periodLabel.replace(/ /g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded statement for ${periodLabel}`);
    } catch { toast.error("Failed to download statement"); }
    finally { setDownloadingStmt(null); }
  };

  const markComplete = async () => {
    setMarkingComplete(true);
    try {
      await API.put(`/investors/${investorId}/mark-complete`);
      toast.success("Investor marked as complete");
      loadInvestor();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to mark as complete");
    } finally {
      setMarkingComplete(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!investor) return null;

  const fund = investor.fund || {};
  const workflow = fund.onboarding_workflow || {};
  const investorType = (investor.personal_info || {})["Investor Type"] || (investor.personal_info || {})["Entity Type"] || "";
  const allFields = (workflow.required_fields || []).filter(f => f.field_name !== "Share Class Subscribed");
  const fields = filterByInvestorType(allFields, investorType);
  const documents = investor.documents || [];
  const statusStyle = ONBOARDING_STYLES[investor.onboarding_status] || ONBOARDING_STYLES.pending;
  const StatusIcon = statusStyle.icon;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="investor-detail-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`${basePath}/investors`)} data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-3xl font-bold tracking-tight">{investor.name}</h1>
              <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${statusStyle.bg}`}>
                {investor.onboarding_status?.replace("_", " ")}
              </Badge>
            </div>
            <p className="text-muted-foreground text-sm mt-1">{investor.email} | {fund.name}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-muted-foreground">Investor ID:</span>
              {canEdit ? (
                <Input
                  value={investorDisplayId}
                  onChange={(e) => setInvestorDisplayId(e.target.value)}
                  className="h-6 text-xs w-32 bg-secondary/50 px-2 py-0"
                  data-testid="investor-display-id-input"
                />
              ) : (
                <span className="text-xs font-mono">{investorDisplayId}</span>
              )}
              {canEdit && (
                <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={async () => {
                  try {
                    await API.put(`/investors/${investorId}`, { investor_display_id: investorDisplayId });
                    toast.success("Investor ID updated");
                  } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
                }} data-testid="save-investor-id-btn">
                  <Save className="w-3 h-3 mr-1" /> Save ID
                </Button>
              )}
            </div>
            {isSA && investor.owner && (
              <div className="flex items-center gap-2 mt-1" data-testid="owner-info">
                <span className="text-xs text-muted-foreground">Owner:</span>
                {investor.owner_id === user?.id ? (
                  <Select value={investor.owner_id} onValueChange={confirmOwnerChange} disabled={ownerChanging}>
                    <SelectTrigger className="h-6 text-xs w-auto min-w-[160px] bg-secondary/50 border-border/50 px-2 py-0" data-testid="owner-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {superAdmins.map(sa => (
                        <SelectItem key={sa.id} value={sa.id}>{sa.name} ({sa.email})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-xs font-medium">{investor.owner.name} ({investor.owner.email})</span>
                )}
              </div>
            )}
          </div>
        </div>
        {canEdit && investor.onboarding_status === "submitted" && (
          <Button onClick={() => setEmailConfirm({ open: true, description: "Marking as complete will send an email notification to the investor. Continue?", action: markComplete })} disabled={markingComplete} className="gap-1.5" data-testid="mark-complete-btn">
            <CheckCircle2 className="w-4 h-4" />
            {markingComplete ? "Marking..." : "Mark as Complete"}
          </Button>
        )}
      </div>

      {/* FM read-only banner — appears when the fund's "FM Can Edit Investor Details" toggle is OFF */}
      {isFM && !fmEditAllowed && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 mb-4" data-testid="fm-readonly-banner">
          <p className="text-xs text-amber-300">
            <span className="font-semibold uppercase tracking-wider mr-1.5">Read-only</span>
            Fund Manager editing of investor details is disabled for this fund. Contact the Super Admin to enable it from Fund Settings → Fund Config.
          </p>
        </div>
      )}

      {/* Login Account Card (SA only - MP-1) */}
      {isSA && userAccount && (
        <Card className="border-border" data-testid="login-account-card">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${userAccount.has_account ? (userAccount.user?.status === "active" ? "bg-emerald-500/10" : "bg-red-500/10") : "bg-secondary/50"}`}>
                <Shield className={`w-4 h-4 ${userAccount.has_account ? (userAccount.user?.status === "active" ? "text-emerald-400" : "text-red-400") : "text-muted-foreground"}`} />
              </div>
              <div>
                <p className="text-sm font-medium">Login Account</p>
                {userAccount.has_account ? (
                  <p className="text-xs text-muted-foreground">
                    {userAccount.user?.email} &mdash;
                    <Badge variant="outline" className={`ml-1.5 text-[10px] uppercase tracking-wider ${userAccount.user?.status === "active" ? "text-emerald-400 border-emerald-400/30" : "text-red-400 border-red-400/30"}`}>
                      {userAccount.user?.status}
                    </Badge>
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">No login account linked</p>
                )}
              </div>
            </div>
            {userAccount.has_account && (
              <Button
                variant={userAccount.user?.status === "active" ? "destructive" : "default"}
                size="sm"
                className="h-8 text-xs"
                disabled={userAccountLoading}
                onClick={() => {
                  const isActive = userAccount.user?.status === "active";
                  setLoginConfirm({
                    open: true,
                    title: isActive ? "Deactivate Login" : "Reactivate Login",
                    description: isActive
                      ? `This will prevent ${investor.name} from logging in. They will not be able to access the portal until reactivated.`
                      : `This will allow ${investor.name} to log in again and access the investor portal.`,
                    action: toggleLoginStatus,
                  });
                }}
                data-testid="toggle-login-btn"
              >
                {userAccountLoading ? "Processing..." : (
                  userAccount.user?.status === "active" ? (
                    <><UserX className="w-3.5 h-3.5 mr-1.5" /> Deactivate Login</>
                  ) : (
                    <><UserCheck className="w-3.5 h-3.5 mr-1.5" /> Reactivate Login</>
                  )
                )}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="info" className="space-y-6">
        <TabsList className="bg-secondary/50 border border-border" data-testid="investor-tabs">
          <TabsTrigger value="info">Information</TabsTrigger>
          <TabsTrigger value="distribution">Distribution List</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="reporting">Reporting</TabsTrigger>
          <TabsTrigger value="fatca" data-testid="tab-fatca">FATCA</TabsTrigger>
          <TabsTrigger value="crs" data-testid="tab-crs">CRS</TabsTrigger>
        </TabsList>

        {/* Info Tab */}
        <TabsContent value="info">
          <Card>
            <CardHeader className="p-6 pb-3">
              <CardTitle className="font-heading text-lg">Personal Information</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {fields.filter(f => f.field_name !== "Tax ID / SSN").map((field, i) => (
                  <div key={field.id || i} className="space-y-2">
                    <Label className="flex items-center gap-1.5">
                      {field.field_name}
                      {field.required && <span className="text-amber-400">*</span>}
                    </Label>
                    {field.field_type === "select" ? (
                      <Select
                        value={personalInfo[field.field_name] || ""}
                        onValueChange={(v) => setPersonalInfo(prev => ({ ...prev, [field.field_name]: v }))}
                        disabled={!canEdit}
                      >
                        <SelectTrigger className="bg-secondary/50">
                          <SelectValue placeholder={`Select ${field.field_name}`} />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {(field.options || []).map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        type={field.field_type === "date" ? "date" : field.field_type === "number" ? "number" : "text"}
                        value={personalInfo[field.field_name] || ""}
                        onChange={(e) => setPersonalInfo(prev => ({ ...prev, [field.field_name]: e.target.value }))}
                        className="bg-secondary/50"
                        disabled={!canEdit}
                        data-testid={`info-field-${field.field_name.toLowerCase().replace(/\s+/g, "-")}`}
                      />
                    )}
                  </div>
                ))}
              </div>
              {canEdit && (
                <div className="flex justify-end pt-2">
                  <Button onClick={saveDetails} disabled={saving} data-testid="save-info-button">
                    <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Offering Subscribed */}
          {(() => {
            const fundOfferings = (fund?.offerings || []).map(o => o.name).filter(Boolean);
            return fundOfferings.length > 0 ? (
              <Card className="mt-4" data-testid="offering-subscribed-card">
                <CardHeader className="p-6 pb-3">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Tag className="w-5 h-5 text-primary" /> Offering Subscribed
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">Select which offerings this investor is subscribed to</p>
                </CardHeader>
                <CardContent className="p-6 pt-0">
                  <div className="flex flex-wrap gap-3">
                    {fundOfferings.map((cls) => (
                      <label
                        key={cls}
                        className={`flex items-center gap-2.5 px-4 py-2.5 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedClasses.includes(cls)
                            ? "border-primary bg-primary/10"
                            : "border-border hover:border-primary/30"
                        }`}
                        data-testid={`offering-option-${cls.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <Checkbox
                          checked={selectedClasses.includes(cls)}
                          onCheckedChange={() => {
                            setSelectedClasses(prev =>
                              prev.includes(cls) ? prev.filter(c => c !== cls) : [...prev, cls]
                            );
                          }}
                          disabled={!canEdit}
                        />
                        <span className="text-sm font-medium">{cls}</span>
                      </label>
                    ))}
                  </div>
                  {selectedClasses.length > 0 && (
                    <div className="flex gap-1.5 mt-3">
                      {selectedClasses.map(cls => (
                        <Badge key={cls} variant="secondary" className="text-xs">{cls}</Badge>
                      ))}
                    </div>
                  )}
                  {canEdit && (
                    <div className="flex justify-end pt-4">
                      <Button onClick={saveOfferings} disabled={saving} data-testid="save-offerings-button">
                        <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Offerings"}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : null;
          })()}
          {false && <SubAgreementExtractCard investorId={investorId} extract={investor.sub_agreement_extract}
            fieldNames={fields.map(f => f.field_name)} offerings={(fund?.offerings || []).map(o => o.name).filter(Boolean)}
            canEdit={canEdit} onUpdated={loadInvestor} />}
        </TabsContent>
        <TabsContent value="distribution">
          <Card data-testid="distribution-card">
            <CardHeader className="p-6 pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Mail className="w-5 h-5 text-primary" /> Distribution Emails
                </CardTitle>
                {canEdit && (
                  <Button variant="secondary" size="sm" onClick={addDistEmail} className="border border-border" data-testid="add-dist-email-button">
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-2">
              {distEmails.map((de, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30 border border-border/50" data-testid={`dist-email-row-${i}`}>
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Input
                      placeholder="email@example.com"
                      type="email"
                      value={de.email}
                      onChange={(e) => updateDistEmail(i, "email", e.target.value)}
                      className="bg-secondary/50 text-sm"
                      disabled={!canEdit}
                    />
                    <Select value={de.label} onValueChange={(v) => updateDistEmail(i, "label", v)} disabled={!canEdit}>
                      <SelectTrigger className="bg-secondary/50 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {LABEL_OPTIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {canEdit && (
                    <Button variant="ghost" size="icon" className="w-7 h-7 text-muted-foreground hover:text-red-400" onClick={() => removeDistEmail(i)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              ))}
              {distEmails.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No distribution emails</p>}
              <Separator className="my-3" />
              <div className="flex items-center justify-between py-2" data-testid="outgoing-email-toggle-section">
                <div>
                  <p className="text-sm font-medium">Activate Outgoing Emails</p>
                  <p className="text-xs text-muted-foreground">Enable email notifications for this investor</p>
                </div>
                <Switch checked={outgoingEmailEnabled} onCheckedChange={v => { setOutgoingEmailEnabled(v); }} disabled={!canEdit} data-testid="outgoing-email-toggle" />
              </div>
              {canEdit && distEmails.length > 0 && (
                <div className="flex justify-end pt-2">
                  <Button onClick={saveDetails} disabled={saving} data-testid="save-dist-button">
                    <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <Card data-testid="documents-card">
            <CardHeader className="p-6 pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" /> Required Documents
                </CardTitle>
                <div className="flex items-center gap-2">
                  <StatusIcon className={`w-4 h-4 ${statusStyle.color}`} />
                  <span className="text-sm text-muted-foreground">
                    {documents.filter(d => d.status === "uploaded").length}/{documents.length} uploaded
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-3">
              {documents.map((doc) => {
                // Build the files list, falling back to a synthesized single entry for legacy slots.
                const slotFiles = (doc.files && doc.files.length > 0)
                  ? doc.files
                  : (doc.stored_name ? [{ file_id: "legacy", file_name: doc.file_name, stored_name: doc.stored_name, uploaded_at: doc.uploaded_at }] : []);
                return (
                  <div key={doc.doc_id} className="flex flex-col gap-3 p-4 rounded-lg bg-secondary/30 border border-border/50" data-testid={`doc-row-${doc.doc_id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${doc.status === "uploaded" ? "bg-emerald-500/10" : "bg-amber-500/10"}`}>
                          {doc.status === "uploaded" ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Clock className="w-4 h-4 text-amber-400" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{doc.doc_name}</p>
                          {slotFiles.length > 0 && (
                            <p className="text-[10px] text-muted-foreground/70">{slotFiles.length} file{slotFiles.length === 1 ? "" : "s"} uploaded</p>
                          )}
                        </div>
                        {doc.required && <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-500/20 ml-2">Required</Badge>}
                      </div>
                      {canEdit && (
                        <div className="flex items-center gap-2">
                          <input
                            type="file"
                            multiple
                            className="hidden"
                            ref={(el) => { fileRefs.current[doc.doc_id] = el; }}
                            onChange={(e) => {
                              if (e.target.files?.length) uploadDoc(doc.doc_id, e.target.files);
                              e.target.value = "";
                            }}
                          />
                          <Button
                            variant="secondary"
                            size="sm"
                            className="border border-border"
                            onClick={() => fileRefs.current[doc.doc_id]?.click()}
                            disabled={uploading[doc.doc_id]}
                            data-testid={`upload-doc-${doc.doc_id}`}
                          >
                            <Upload className="w-4 h-4 mr-1" /> {uploading[doc.doc_id] ? "Uploading..." : slotFiles.length > 0 ? "Add more" : "Upload"}
                          </Button>
                        </div>
                      )}
                    </div>
                    {slotFiles.length > 0 && (
                      <div className="space-y-1.5 pl-11" data-testid={`doc-files-${doc.doc_id}`}>
                        {slotFiles.map((f) => (
                          <div key={f.file_id || f.stored_name} className="flex items-center justify-between gap-2 text-xs bg-card/50 border border-border/50 rounded-md px-3 py-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                              <span className="truncate" title={f.file_name}>{f.file_name || "(unnamed)"}</span>
                              {f.uploaded_at && (
                                <span className="text-muted-foreground/60 flex-shrink-0">· {new Date(f.uploaded_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button variant="ghost" size="icon" className="h-7 w-7" title="Preview" onClick={() => previewDoc(doc.doc_id, f.file_id)} data-testid={`preview-file-${f.file_id}`}>
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" title="Download" onClick={() => downloadDoc(doc.doc_id, f.file_name, f.file_id)} data-testid={`download-file-${f.file_id}`}>
                                <Download className="w-3.5 h-3.5" />
                              </Button>
                              {canEdit && f.file_id && f.file_id !== "legacy" && (
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-400" title="Remove" onClick={() => deleteDocFile(doc.doc_id, f.file_id, f.file_name)} data-testid={`delete-file-${f.file_id}`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {documents.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No documents required</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="fatca">
          <TaxFormTab section="fatca" investorType={investorType} data={investor.tax_forms?.fatca} canEdit={canEdit} onSave={saveTaxForm}
            headerAction={<SectionPdfButton section="fatca" investorId={investorId} hasSource={!!investor.tax_forms_source?.stored_name} testId="fatca-download-pdf-btn" />} />
        </TabsContent>
        <TabsContent value="crs">
          <TaxFormTab section="crs" investorType={investorType} data={investor.tax_forms?.crs} canEdit={canEdit} onSave={saveTaxForm}
            headerAction={<SectionPdfButton section="crs" investorId={investorId} hasSource={!!investor.tax_forms_source?.stored_name} testId="crs-download-pdf-btn" />} />
        </TabsContent>

        {/* Reporting Tab */}
        <TabsContent value="reporting">
          <Card data-testid="reporting-card">
            <CardHeader className="p-6 pb-3">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" /> Investor Statements
              </CardTitle>
              <p className="text-xs text-muted-foreground">Download capital account statements for each reporting period</p>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              {statementPeriods.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground text-sm">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  No statements available yet. Statements are generated when performance data is uploaded.
                </div>
              ) : (
                <div className="space-y-2">
                  {statementPeriods.map(p => (
                    <div key={p.id} className="flex items-center justify-between p-3.5 rounded-lg bg-secondary/30 border border-border/50 group hover:border-primary/30 transition-colors" data-testid={`inv-statement-${p.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                          <FileText className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{p.label}</p>
                          <p className="text-xs text-muted-foreground">Capital Account Statement</p>
                        </div>
                        <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${p.status === "released" ? "text-emerald-400 border-emerald-400/30" : p.status === "approved" ? "text-blue-400 border-blue-400/30" : p.status === "pending_approval" ? "text-indigo-400 border-indigo-400/30" : p.status === "reviewed" ? "text-sky-400 border-sky-400/30" : p.status === "rejected" ? "text-red-400 border-red-400/30" : "text-amber-400 border-amber-400/30"}`}>
                          {p.status === "released" ? "Released" : p.status === "approved" ? "Approved" : p.status === "pending_approval" ? "Pending Approval" : p.status === "reviewed" ? "Reviewed" : p.status === "rejected" ? "Rejected" : "Prepared"}
                        </Badge>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="h-8 text-xs border border-border opacity-60 group-hover:opacity-100 transition-opacity"
                        onClick={() => downloadStatement(p.id, p.label)}
                        disabled={downloadingStmt === p.id}
                        data-testid={`download-inv-stmt-${p.id}`}
                      >
                        <Download className="w-3.5 h-3.5 mr-1.5" />
                        {downloadingStmt === p.id ? "Downloading..." : "Download PDF"}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <EmailConfirmDialog
        open={emailConfirm.open}
        onOpenChange={(v) => setEmailConfirm(prev => ({ ...prev, open: v }))}
        description={emailConfirm.description}
        onConfirm={() => { if (emailConfirm.action) emailConfirm.action(); }}
      />

      <ConfirmDialog
        open={loginConfirm.open}
        onOpenChange={(v) => setLoginConfirm(prev => ({ ...prev, open: v }))}
        title={loginConfirm.title}
        description={loginConfirm.description}
        confirmLabel={loginConfirm.title?.includes("Deactivate") ? "Deactivate" : "Reactivate"}
        variant={loginConfirm.title?.includes("Deactivate") ? "destructive" : "default"}
        onConfirm={() => { if (loginConfirm.action) loginConfirm.action(); }}
        loading={userAccountLoading}
      />

      <ConfirmDialog
        open={!!pendingOwnerId}
        onOpenChange={(v) => { if (!v) setPendingOwnerId(null); }}
        title="Change Owner"
        description={`Caution: You are about to transfer ownership of this investor to "${superAdmins.find(s => s.id === pendingOwnerId)?.name || "another SA"}". You will no longer be able to manage ownership after this change.`}
        confirmLabel="Change"
        variant="destructive"
        onConfirm={() => changeOwner(pendingOwnerId)}
        loading={ownerChanging}
      />
    </div>
  );
}
